<script setup>
import { toRefs, ref, nextTick, onMounted } from 'vue';
import { gsap } from 'gsap';

import LogoFull from '@/components/logo-full/LogoFull.vue';
import NavigationSubmenu from './NavigationSubmenu.vue';
import TransitionExpand from '@/components/transition-expand/TransitionExpand.vue';
import { GA } from '@/mixins/Ga';
import Caret from '@/icons/caret.svg';
import DemiLogo from '@/icons/demi-logo.svg';

const roots = ref([]);
const rootEl = ref(null);

const props = defineProps({
    opened: {
        type: Boolean,
        default: false,
    },
    menu: {
        type: Object,
        default: () => {},
    },
});

onMounted(() => {
    nextTick(() => {
        for (let i = 0, il = roots.value.length; i < il; ++i) {
            const rootEl = roots.value[i];
            gsap.set(rootEl, { height: 0 });
        }
    });
});

const { opened: menuOpened } = toRefs(props);
const ga = new GA({ category: 'hamburgerMenuMobile', action: 'clicInterne' });

const toggleTopLevelMenu = (link) => {
    const currentLink = link;

    ga.send({ label: currentLink.title });
    currentLink.mobileOpened = !currentLink.mobileOpened;
};
</script>

<template>
    <transition
        ref="rootEl"
        enter-active-class="transition duration-500"
        leave-active-class="transition duration-500"
        enter-from-class="opacity-0 -translate-y-4 xl:translate-y-4"
        enter-to-class="opacity-100 translate-y-0"
        leave-from-class="opacity-100 translate-y-0"
        leave-to-class="opacity-0 -translate-y-4 xl:translate-y-4"
        mode="out-in"
    >
        <div
            v-show="menuOpened"
            class="NavigationLightbox absolute inset-x-0 top-0 z-50 flex h-screen overflow-auto bg-bright-indigo text-white transition duration-500 xl:left-auto xl:right-8 xl:top-22 xl:h-auto xl:w-[875px] xl:overflow-visible xl:rounded-xl xl:bg-white"
            :data-lenis-prevent="xs || md"
        >
            <div
                class="NavigationLightbox__right flex flex-row flex-wrap justify-center text-light-indigo xl:flex-col xl:flex-nowrap xl:items-start xl:text-black"
            >
                <template v-if="menu">
                    <div class="NavigationLightbox__main flex w-full shrink-0 flex-wrap pt-18 md:w-full xl:hidden xl:h-95 xl:shrink xl:flex-col">
                        <div
                            v-for="link in menu.main"
                            :key="link.id"
                            class="w-full"
                        >
                            <button
                                class="group relative w-full cursor-pointer border-b border-solid border-light-blue transition duration-500 first:border-t xl:-ml-18 xl:mb-12 xl:w-126 xl:last:mb-0"
                                :class="{ 'bg-white': link.mobileOpened, 'border-transparent': link.mobileOpened }"
                                v-on:click.stop="toggleTopLevelMenu(link)"
                            >
                                <span class="NavigationLightbox__main-fix flex items-center justify-between p-5">
                                    <!-- <Icon
                                        class="relative mr-4 hidden w-15 text-light-green opacity-0 xl:block"
                                        color="currentcolor"
                                        type="semi-circle"
                                    /> -->
                                    <h3 class="NavigationLightbox__title text-left text-2xl font-medium md:text-4xl xl:text-5xl">
                                        <div
                                            class="text-sm font-medium leading-none"
                                            :class="[link.mobileOpened ? 'text-light-' + link.color : 'text-light-blue']"
                                        >
                                            {{ link.suptitle }}
                                        </div>
                                        {{ link.title }}
                                    </h3>
                                    <Caret
                                        class="h-4 w-auto fill-current transition duration-500"
                                        :class="{ 'rotate-90': !link.mobileOpened, '-rotate-90': link.mobileOpened }"
                                    />
                                </span>
                            </button>
                            <TransitionExpand>
                                <div
                                    v-show="link.mobileOpened"
                                    ref="roots"
                                    class="sousmenu__inner relative z-10 w-full overflow-hidden"
                                >
                                    <NavigationSubmenu
                                        class="relative flex flex-col justify-between gap-6 bg-white px-3 xl:flex-row"
                                        :link="link"
                                    />
                                </div>
                            </TransitionExpand>
                        </div>
                    </div>

                    <div
                        class="NavigationLightbox__secondary flex w-full shrink-0 flex-col items-end xl:w-auto xl:shrink xl:flex-row xl:overflow-hidden xl:rounded-xl"
                    >
                        <div class="NavigationLightbox__secondary-left order-2 flex w-full flex-wrap xl:order-1 xl:w-full">
                            <div
                                v-for="link in menu.secondary"
                                :key="link.id"
                                class="w-1/2 border-b border-r border-light-blue px-5 py-3 xl:border-bright-blue xl:px-4 xl:py-2"
                            >
                                <router-link
                                    class="can-hover:hover-line relative inline-block"
                                    :to="setUri({ uri: link.uri })"
                                >
                                    <span
                                        class="NavigationLightbox__title text-base font-medium md:text-lg"
                                        v-on:click="ga.send({ label: link.title })"
                                    >
                                        {{ link.title }}
                                    </span>
                                </router-link>
                            </div>
                        </div>
                    </div>

                    <div class="NavigationLightbox__bottom absolute left-0 top-0 mb-4 flex w-full items-end justify-between px-6 xl:hidden">
                        <router-link :to="{ name: 'home.' + lang }">
                            <LogoFull class="mt-3 h-auto w-8" />
                        </router-link>
                        <button
                            class="z-50 ml-auto h-8 w-8 cursor-pointer rounded-full bg-dark-blue text-white transition duration-500 md:h-10 md:w-10 xl:h-11 xl:w-11"
                            v-on:click="menuOpened = false"
                        >
                            <svg
                                viewBox="0 0 50 50"
                                class="w-full"
                                fill-rule="evenodd"
                            >
                                <path
                                    class="fill-current"
                                    d="M32.6,19.5L18.8,32c-0.4,0.4-1.1,0.4-1.4,0c-0.4-0.4-0.3-1.1,0.1-1.4L31.2,18c0.4-0.4,1.1-0.4,1.4,0 C33,18.5,33,19.1,32.6,19.5z"
                                />
                                <path
                                    class="fill-current"
                                    d="M30.6,32.5L17.9,18.9c-0.4-0.4-0.4-1.1,0-1.4s1-0.3,1.4,0.1l12.8,13.5c0.4,0.4,0.4,1.1,0,1.4C31.7,32.9,31,32.9,30.6,32.5z"
                                />
                            </svg>
                        </button>
                    </div>

                    <div
                        v-if="menu.infos"
                        class="NavigationLightbox__credits p-6 text-center text-light-blue xl:hidden"
                    >
                        {{ menu.infos.copyright }}
                    </div>
                </template>
            </div>
        </div>
    </transition>
</template>
